import { useState } from "react";
import { Button } from "@/components/_components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/_components/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Edit } from "lucide-react";
import toast from "react-hot-toast";
import * as api from "@/lib/api/client";
import type { RoleDto } from "@/lib/api/types";

interface CreateRoleDialogProps {
  role?: RoleDto;
  onSuccess: () => void;
  trigger?: React.ReactNode;
}

const CreateRoleDialog: React.FC<CreateRoleDialogProps> = ({
  role,
  onSuccess,
  trigger,
}) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: role?.name || "",
  });

  const isEditing = !!role;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Role name is required");
      return;
    }

    try {
      setIsLoading(true);
      
      if (isEditing && role?.id) {
        await api.updateRole(role.id, {
          name: formData.name.trim(),
        });
        toast.success("Role updated successfully");
      } else {
        await api.createRole({
          name: formData.name.trim(),
        });
        toast.success("Role created successfully");
      }
      
      setOpen(false);
      setFormData({ name: "" });
      onSuccess();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "An error occurred";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setFormData({ name: role?.name || "" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="gap-2">
            {isEditing ? (
              <>
                <Edit className="h-4 w-4" />
                Edit Role
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                Create Role
              </>
            )}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? "Edit Role" : "Create New Role"}
            </DialogTitle>
            <DialogDescription>
              {isEditing 
                ? "Update the role information below."
                : "Create a new role to manage user permissions."
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Role Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter role name (e.g., ADMIN, USER, MANAGER)"
                required
                disabled={isLoading}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : isEditing ? "Update Role" : "Create Role"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateRoleDialog;